import { useEffect, useRef, useState } from 'react';
import { Card } from '@/components/ui/Card';
import { TextArea } from '@/components/ui/TextArea';
import { Tabs } from '@/components/ui/Tabs';
import { Avatar } from '@/components/ui/Avatar';
import { Icon } from '@/components/ui/Icon';
import { PhoneCell } from '@/components/telephony/PhoneCell';
import { SoftphonePanel } from '../components/SoftphonePanel';
import { WrapupInline } from '../components/WrapupInline';
import type { DispositionOption } from '../components/WrapupInline';
import { useSipStore } from '../stores/sipStore';
import { useSoftphoneWindow } from '../stores/softphoneWindow';
import { agentApi, type ScreenPop } from '@/shared/api/agentApi';
import { toast } from '@/components/ui/Toast';
import { useAuthStore } from '@/shared/stores/auth';

interface LookupResult { name?: string; label?: string; source?: string; confidence?: number; is_spam?: boolean; is_verified?: boolean; contact_id?: number }
interface HistoryCall { id: number; direction: string; status: string; started_at: string; talk_sec?: number }
interface Script { id: number; name: string; description?: string; tree?: { sections?: { title: string; body: string }[] } }

const FALLBACK_DISPOSITIONS: DispositionOption[] = [
  { code: 'deal', label: 'עסקה' },
  { code: 'follow_up', label: 'מעקב' },
  { code: 'not_interested', label: 'לא מעוניין' },
  { code: 'no_answer', label: 'לא ענה' },
  { code: 'wrong_number', label: 'מספר שגוי' },
];

export function ActiveCall() {
  const active = useSipStore((s) => s.active);
  const state = useSipStore((s) => s.state);
  const softphoneFloating = useSoftphoneWindow((s) => s.floating);
  const [tab, setTab] = useState<'customer' | 'script' | 'history' | 'notes'>('customer');
  const [lookup, setLookup] = useState<LookupResult | null>(null);
  const [pop, setPop] = useState<ScreenPop | null>(null);
  const [notes, setNotes] = useState('');
  const [dispositions, setDispositions] = useState<DispositionOption[]>(FALLBACK_DISPOSITIONS);
  const [tagOptions, setTagOptions] = useState<{ slug: string; label: string; color?: string }[]>([]);
  const [history, setHistory] = useState<HistoryCall[]>([]);
  const [scripts, setScripts] = useState<Script[]>([]);
  const user = useAuthStore((s) => s.user);
  const tenant = useAuthStore((s) => s.tenant);
  // Remember the last active call id so wrap-up (after the call ended) can dispose it.
  const lastCallId = useRef<number | null>(null);
  const lastNumber = useRef<string | null>(null);

  // Fill {{placeholder}} tokens in a script line with the live call context.
  function fillScript(text: string): string {
    const vars: Record<string, string> = {
      customer_name: lookup?.name || '',
      customer_phone: active?.number || lastNumber.current || '',
      agent_name: user?.name || '',
      company_name: tenant?.name || '',
      date: new Date().toLocaleDateString('he-IL'),
    };
    return text.replace(/\{\{\s*([a-z_]+)\s*\}\}/gi, (m, k) => {
      const v = vars[k.toLowerCase()];
      return v ? v : m;
    });
  }

  useEffect(() => {
    if (active?.callId) lastCallId.current = active.callId;
    if (active?.number) lastNumber.current = active.number;
  }, [active?.callId, active?.number]);

  useEffect(() => {
    void agentApi.callDispositions().then((d) => { if (d.length) setDispositions(d as DispositionOption[]); }).catch((e) => console.warn('[dispositions] load failed, using fallback', e));
    void agentApi.callTags().then((tg) => setTagOptions(tg)).catch(() => {});
    void agentApi.scripts().then((s) => setScripts(s as Script[])).catch(() => {});
  }, []);

  useEffect(() => {
    void (async () => {
      if (!active?.number) { setPop(null); return; }
      try {
        const r = await agentApi.lookup(active.number);
        setLookup(r as LookupResult);
      } catch {
        setLookup(null);
      }
      // Full CRM "customer file" — same screen-pop the incoming-call modal uses,
      // so the moment a call is answered the agent sees the whole contact card
      // (company, tags, fields, notes), not just a name.
      try {
        setPop(await agentApi.screenPop(active.number));
      } catch {
        setPop(null);
      }
      // Recent calls of this agent (most recent first) — surface ones with
      // the same counterparty number as "previous contact".
      try {
        const res = await agentApi.calls();
        const all = (res.data as (HistoryCall & { from_number?: string; to_number?: string })[]) ?? [];
        setHistory(all.filter((c) => c.from_number === active.number || c.to_number === active.number).slice(0, 10));
      } catch {
        setHistory([]);
      }
    })();
  }, [active?.number]);

  async function saveWrapup(d: { disposition: string; notes: string; callback_at: string | null; tags: string[] }) {
    const callId = lastCallId.current;
    if (!callId) {
      toast('לא ניתן לזהות את השיחה לשמירה', 'danger');
      throw new Error('no call id');
    }
    await agentApi.disposeCall(callId, {
      disposition: d.disposition,
      notes: d.notes || undefined,
      callback_at: d.callback_at,
      tags: d.tags,
    });
  }

  if (!active && state !== 'wrap-up') {
    return (
      <Card>
        <div className="empty">
          <h4>אין שיחה פעילה</h4>
          <p>הסופטפון יתחיל לפעול כשתתחיל לחייג או תקבל שיחה.</p>
        </div>
      </Card>
    );
  }

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: softphoneFloating ? '1fr' : 'minmax(0, 1fr) var(--softphone-w)',
        gap: 'var(--gap)',
        alignItems: 'flex-start',
      }}
    >
      <div className="stack">
        {state === 'wrap-up' ? (
          <WrapupInline dispositions={dispositions} tagOptions={tagOptions} onSave={saveWrapup} phoneNumber={active?.number ?? lastNumber.current} />
        ) : (
          <>
            {/* Customer header */}
            <Card>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <Avatar name={active!.number} size={56} />
                <div style={{ flex: 1 }}>
                  <PhoneCell number={active!.number} lookup={(lookup ?? undefined) as never} />
                  <div style={{ marginTop: 4, fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>
                    {active!.direction === 'inbound' ? 'שיחה נכנסת' : 'שיחה יוצאת'} · {fmtDur(active!.durationSec)}
                  </div>
                </div>
                <Icon name="phone" size={28} color="var(--ok)" />
              </div>
            </Card>

            {/* Tabs */}
            <Card padded={false}>
              <Tabs
                items={[
                  { id: 'customer', label: 'לקוח' },
                  { id: 'script', label: 'תסריט' },
                  { id: 'history', label: 'היסטוריה' },
                  { id: 'notes', label: 'הערות' },
                ]}
                value={tab}
                onChange={(id) => setTab(id as typeof tab)}
              />
              <div style={{ padding: 'var(--pad-card)' }}>
                {tab === 'customer' && (
                  <div className="stack" style={{ gap: 6 }}>
                    <Row k="מספר" v={active!.number} mono />
                    {(pop?.contact?.name || lookup?.name) && <Row k="שם" v={pop?.contact?.name || lookup!.name!} />}
                    {pop?.contact?.company && <Row k="חברה" v={pop.contact.company} />}
                    {pop?.contact?.email && <Row k="אימייל" v={pop.contact.email} />}
                    {lookup?.label && <Row k="תווית" v={lookup.label} />}
                    {lookup?.source && <Row k="מקור זיהוי" v={lookup.source} />}
                    {lookup?.is_spam && <Row k="התראה" v="מסומן כספאם ⚠️" />}

                    {(pop?.contact?.tags?.length ?? 0) > 0 && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, paddingTop: 4 }}>
                        {pop!.contact!.tags.map((tg) => (
                          <span key={tg} style={{ fontSize: 'var(--t-xs)', background: 'var(--accent-soft)', color: 'var(--accent-ink)', borderRadius: 'var(--r-1)', padding: '1px 8px' }}>{tg}</span>
                        ))}
                      </div>
                    )}

                    {Object.entries(pop?.contact?.fields ?? {}).slice(0, 6).map(([k, v]) => (
                      <Row key={k} k={k} v={String(v)} />
                    ))}

                    {(pop?.contact?.notes?.length ?? 0) > 0 && (
                      <div style={{ borderTop: '1px solid var(--line)', paddingTop: 6 }}>
                        <div style={{ color: 'var(--muted)', fontSize: 'var(--t-xs)', marginBottom: 4 }}>הערות</div>
                        {pop!.contact!.notes.slice(0, 3).map((n, i) => (
                          <div key={i} style={{ fontSize: 'var(--t-sm)', color: 'var(--text-2)', marginBottom: 4 }}>
                            • {n.body}
                            {n.created_at && <span style={{ color: 'var(--faint)', fontSize: 'var(--t-xs)' }}> ({new Date(n.created_at).toLocaleDateString('he-IL')})</span>}
                          </div>
                        ))}
                      </div>
                    )}

                    {!pop?.contact && !lookup?.name && !lookup?.label && (
                      <p style={{ margin: 0, color: 'var(--muted)', fontSize: 'var(--t-sm)' }}>המספר אינו מזוהה ב-CRM.</p>
                    )}
                  </div>
                )}
                {tab === 'script' && (
                  scripts.length === 0 ? (
                    <p style={{ color: 'var(--muted)' }}>אין תסריטים זמינים.</p>
                  ) : (
                    <div className="stack">
                      {scripts.map((s) => (
                        <details key={s.id}>
                          <summary style={{ cursor: 'pointer', fontWeight: 600 }}>{s.name}</summary>
                          {(s.tree?.sections ?? []).length === 0 ? (
                            <p style={{ color: 'var(--muted)', fontSize: 'var(--t-sm)', margin: '6px 0' }}>{s.description || 'אין שלבים.'}</p>
                          ) : (
                            <div className="stack" style={{ marginTop: 6 }}>
                              {(s.tree?.sections ?? []).map((sec, i) => (
                                <div key={i} style={{ padding: 8, background: 'var(--surface-2)', borderRadius: 'var(--r-2)' }}>
                                  <strong style={{ fontSize: 'var(--t-sm)' }}>{fillScript(sec.title)}</strong>
                                  <div style={{ fontSize: 'var(--t-sm)', color: 'var(--text-2)', whiteSpace: 'pre-wrap' }}>{fillScript(sec.body)}</div>
                                </div>
                              ))}
                            </div>
                          )}
                        </details>
                      ))}
                    </div>
                  )
                )}
                {tab === 'history' && (
                  history.length === 0 ? (
                    <p style={{ color: 'var(--muted)' }}>אין שיחות קודמות עם מספר זה.</p>
                  ) : (
                    <div className="stack" style={{ gap: 4 }}>
                      {history.map((c) => (
                        <div key={c.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 12px', background: 'var(--surface-2)', borderRadius: 'var(--r-2)', fontSize: 'var(--t-sm)' }}>
                          <span>{c.direction === 'inbound' ? '↙ נכנסת' : '↗ יוצאת'} · {c.status}</span>
                          <span style={{ color: 'var(--muted)', fontSize: 'var(--t-xs)' }}>{new Date(c.started_at).toLocaleString('he-IL', { dateStyle: 'short', timeStyle: 'short' })}</span>
                        </div>
                      ))}
                    </div>
                  )
                )}
                {tab === 'notes' && (
                  <div className="stack">
                    <TextArea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      rows={6}
                      placeholder="הערות בזמן השיחה... (יישמרו בסיכום בסיום השיחה)"
                    />
                    <p style={{ margin: 0, color: 'var(--muted)', fontSize: 'var(--t-xs)' }}>
                      ההערות יישמרו אוטומטית בשדה הסיכום כשתסיים את השיחה.
                    </p>
                  </div>
                )}
              </div>
            </Card>
          </>
        )}
      </div>

      {!softphoneFloating && (
        <div style={{ position: 'sticky', top: 16 }}>
          <SoftphonePanel />
        </div>
      )}
    </div>
  );
}

function Row({ k, v, mono }: { k: string; v: string; mono?: boolean }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: '1px solid var(--line)' }}>
      <span style={{ color: 'var(--muted)' }}>{k}</span>
      <span dir={mono ? 'ltr' : undefined} style={{ fontFamily: mono ? 'var(--font-mono)' : undefined }}>{v}</span>
    </div>
  );
}

function fmtDur(s: number): string {
  const m = Math.floor(s / 60);
  const r = (s % 60).toString().padStart(2, '0');
  return `${m}:${r}`;
}
